/**
 * Integration utility functions
 */

import type { Integration, LocalizedText, Language } from '@/types/integrations'
import { integrationDetails } from './integrationDetails'

/**
 * Get localized text based on language
 */
export const getLocalizedText = (text: LocalizedText | undefined, language: Language): string => {
  if (!text) return ''
  return text[language] || text.en
}

/**
 * Detect current language from translation
 * @param translatedWord - A word that should be different in Dutch (e.g., "Save" vs "Opslaan")
 */
export const getCurrentLanguage = (isDutch: boolean): Language => {
  return isDutch ? 'nl-NL' : 'en'
}

/**
 * Get category color based on category name
 */
export const getCategoryColor = (category: string): string => {
  const categoryColors: Record<string, string> = {
    // English
    Reservations: '#4f46e5', // Indigo
    Events: '#059669', // Green
    Messaging: '#0891b2', // Cyan
    'External Tools': '#7c3aed', // Violet
    // Dutch
    Reserveringen: '#4f46e5', // Indigo
    Evenementen: '#059669', // Green
    Berichten: '#0891b2', // Cyan
    'Externe tools': '#7c3aed', // Violet
  }
  return categoryColors[category] || '#368F8B' // Default to primary teal
}

/**
 * Get delay label with localization
 */
export const getDelayLabel = (minutes: string | number, language: Language = 'en'): string => {
  const delayOptions: Record<string, LocalizedText> = {
    '0': { en: 'No delay', 'nl-NL': 'Geen vertraging' },
    '15': { en: '15 minutes', 'nl-NL': '15 minuten' },
    '30': { en: '30 minutes', 'nl-NL': '30 minuten' },
    '45': { en: '45 minutes', 'nl-NL': '45 minuten' },
    '60': { en: '1 hour', 'nl-NL': '1 uur' },
    '90': { en: '1.5 hours', 'nl-NL': '1,5 uur' },
    '120': { en: '2 hours', 'nl-NL': '2 uur' },
    '180': { en: '3 hours', 'nl-NL': '3 uur' },
  }
  const option = delayOptions[String(minutes)]
  return option ? getLocalizedText(option, language) : `${minutes} min`
}

/**
 * Get option expiration label with localization
 */
export const getOptionExpirationLabel = (
  minutes: string | number,
  language: Language = 'en'
): string => {
  const expirationOptions: Record<string, LocalizedText> = {
    '30': { en: '30 minutes', 'nl-NL': '30 minuten' },
    '60': { en: '1 hour', 'nl-NL': '1 uur' },
    '90': { en: '1.5 hours', 'nl-NL': '1,5 uur' },
    '120': { en: '2 hours', 'nl-NL': '2 uur' },
    '180': { en: '3 hours', 'nl-NL': '3 uur' },
    '240': { en: '4 hours', 'nl-NL': '4 uur' },
    '360': { en: '6 hours', 'nl-NL': '6 uur' },
    '720': { en: '12 hours', 'nl-NL': '12 uur' },
    '1440': { en: '24 hours', 'nl-NL': '24 uur' },
  }
  const option = expirationOptions[String(minutes)]
  return option ? getLocalizedText(option, language) : `${minutes} min`
}

/**
 * Check if an integration with the same category is already added
 */
export const isIntegrationAdded = (
  integrationKey: string,
  existingIntegrations: Integration[],
  currentLanguage: Language = 'en'
): boolean => {
  const newIntegrationCategory = integrationDetails[integrationKey]?.category
  if (!newIntegrationCategory) return false

  const newCategoryText = getLocalizedText(newIntegrationCategory, currentLanguage)

  return existingIntegrations.some((integration) => {
    if (!integration || !integration.name) return false
    const existingCategory = integrationDetails[integration.name]?.category
    if (!existingCategory) return false
    const existingCategoryText = getLocalizedText(existingCategory, currentLanguage)
    return existingCategoryText === newCategoryText
  })
}

/**
 * Get config summary for display in integration card
 */
export const getConfigSummary = (
  integration: Integration,
  language: Language = 'en'
): { required: Array<{ label: string; value: string }>; optional: Array<{ label: string; value: string }> } => {
  if (!integration || !integration.name) {
    return { required: [], optional: [] }
  }

  const config = integration.config || {}
  const details = integrationDetails[integration.name]

  if (!details) {
    return { required: [], optional: [] }
  }

  const requiredItems: Array<{ label: string; value: string }> = []
  const optionalItems: Array<{ label: string; value: string }> = []

  details.configFields.forEach((field) => {
    const value = config[field.key]

    // Skip if no value is set or if it's an API key or auth field
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      field.key === 'api_key' ||
      field.key.startsWith('auth.')
    ) {
      return
    }

    const localizedLabel = getLocalizedText(field.label, language)
    const label = `${localizedLabel}:`
    let displayValue: string

    // Format the display value based on field type
    switch (field.key) {
      case 'option_expiration_minutes':
        displayValue = getOptionExpirationLabel(value, language)
        break
      case 'enable_messaging':
      case 'send_option_reminders':
        displayValue = value
          ? language === 'nl-NL'
            ? 'Aan'
            : 'On'
          : language === 'nl-NL'
            ? 'Uit'
            : 'Off'
        break
      case 'allowed_delay_minutes':
      case 'connect_to_staff_minutes':
      case 'imminent_window_minutes':
        displayValue = getDelayLabel(value, language)
        break
      default:
        if (field.type === 'boolean') {
          displayValue = value
            ? language === 'nl-NL'
              ? 'Aan'
              : 'On'
            : language === 'nl-NL'
              ? 'Uit'
              : 'Off'
        } else {
          displayValue = String(value)
        }
    }

    if (field.required) {
      requiredItems.push({ label, value: displayValue })
    } else {
      optionalItems.push({ label, value: displayValue })
    }
  })

  // Limit total items to 6
  const totalItems = requiredItems.length + optionalItems.length
  if (totalItems > 6) {
    const requiredCount = Math.min(requiredItems.length, 4)
    const remainingSlots = 6 - requiredCount

    return {
      required: requiredItems.slice(0, requiredCount),
      optional: optionalItems.slice(0, remainingSlots),
    }
  }

  return { required: requiredItems, optional: optionalItems }
}

/**
 * Parse text with URLs and return parts
 */
export const parseTextWithLinks = (
  text: string
): Array<{ type: 'text' | 'link'; content: string; href?: string }> => {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi
  const parts: Array<{ type: 'text' | 'link'; content: string; href?: string }> = []
  let lastIndex = 0
  let match

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      })
    }

    let url = match[0]
    if (!url.startsWith('http')) {
      url = 'https://' + url
    }

    parts.push({
      type: 'link',
      content: match[0],
      href: url,
    })

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    })
  }

  return parts
}

/**
 * Clean integration config by removing empty values
 */
export const cleanIntegrationConfig = (config: any): any => {
  if (!config || typeof config !== 'object') {
    return config
  }

  const cleanedConfig = { ...config }

  Object.keys(cleanedConfig).forEach((key) => {
    const value = cleanedConfig[key]

    if (value === '' || value === null || value === undefined) {
      delete cleanedConfig[key]
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      const cleanedNestedObject = cleanIntegrationConfig(value)
      if (Object.keys(cleanedNestedObject).length > 0) {
        cleanedConfig[key] = cleanedNestedObject
      } else {
        delete cleanedConfig[key]
      }
    }
  })

  return cleanedConfig
}

/**
 * Validate HTTP URL
 */
export const isValidHttpUrl = (value: string): boolean => {
  try {
    const u = new URL(value)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}
